<template>
  <hr-menu-chip
    :model-value="modelValue"
    :options="options"
    default-value="normal"
    data-e2e="chip-session-type"
    @update:model-value="$emit('update:modelValue', $event)"
  />
</template>

<script>
import { HrMenuChip } from 'src/design-system';
import { auth } from 'src/store/auth';
import { mapState } from 'pinia';

/**
 * The session type, as an attribute of the plan or prototype being edited.
 *
 * It lives here rather than inside `SessionSettingsEditor` because it is a property of the record,
 * not a field of the settings tab — both dialogs put it beside their title. It is a component rather
 * than a computed copied into each of them because the rule about which types a profile may hold is
 * a rule, and two copies of a rule become two answers.
 *
 * NOTHING BELOW IT IS FILTERED. The type reshapes the settings tab, but the chip states what the
 * record IS; the reshaping follows from that rather than being what the control does.
 */
export default {
  name: 'SessionTypeChip',

  components: { HrMenuChip },

  props: {
    /** The chosen session type. */
    modelValue: {
      type: String,
      default: 'normal',
    },

    /**
     * The client the record belongs to, when it has one. A prototype has none, so it may hold any
     * type; a plan is bound to one profile and may hold only the types that profile allows.
     */
    client: {
      type: String,
      default: null,
    },
  },

  emits: ['update:modelValue'],

  computed: {
    ...mapState(auth, ['currentClient']),

    /**
     * A prototype has no client and is profile-agnostic, so it takes the neutral label. Without the
     * guard the label would follow whichever profile was visited last, since `currentClient` outlives
     * the route that set it and the prototype page sits outside any client.
     */
    isStaffProfile() {
      return Boolean(this.client) && this.currentClient?.type === 'staff';
    }, // isStaffProfile()

    options() {
      const profileType = this.currentClient?.type ?? 'client';

      return [
        // The same stored value means different work depending on whose profile it is: therapy for a
        // client, competency for a staff member. One label for both described only the first.
        { value: 'normal', allowedTypes: ['client', 'staff'], key: this.isStaffProfile ? 'NormalStaff' : 'Normal' },
        { value: 'remote_async', allowedTypes: ['client'], key: 'RemoteAsync' },
        { value: 'training', allowedTypes: ['staff'], key: 'Training' },
      ]
        // The stored value always survives the filter, so the chip states what the record holds even
        // when the profile would not offer that type: a plan whose type predates a profile change,
        // or any plan at all before `currentClient` has loaded. Filtered out, the chip has no
        // matching option and renders with no label, which reads as a plan with no type.
        .filter(({ value, allowedTypes }) => (
          value === this.modelValue || !this.client || allowedTypes.includes(profileType)
        ))
        .map(({ value, key }) => ({
          value,
          label: this.$t(`sessions.modals.scheduleSession.sessionTypesOpts${key}`),
          caption: this.$t(`sessions.modals.scheduleSession.sessionTypesCaption${key}`),
        }));
    }, // options()
  },
};
</script>
